import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import clinicalReportsService from '../../../services/clinicalReportsService';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { HEAD_DOCTOR_PATHS } from '../../doctor/doctorPaths';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader, HeadDoctorStatCard } from '../ui';
import Icon from '../../common/Icons';
import { todayYmd, daysAgoYmd } from '../../../utils/appTimezone';
import {
  buildSynthesisRows,
  countDaysInclusive,
  nursingActsTotal,
  statusCount
} from '../reports/synthesisTableModel';
import DashboardHighlights from './DashboardHighlights';
import DashboardActivityPanel from './DashboardActivityPanel';
import '../HeadDoctorDashboard.css';

const H = HEAD_DOCTOR_COPY.DASHBOARD;
const PERIOD_PRESETS = [
  { key: '7d', days: 7, labelKey: 'PERIOD_7D' },
  { key: '30d', days: 30, labelKey: 'PERIOD_30D' }
];

function StatLink({ to, children }) {
  return (
    <Link to={to} className="hd-dash-stat-link">
      {children}
    </Link>
  );
}

export default function HeadDoctorDashboardPage() {
  const { user } = useAuth();
  const { workSiteId, workSites, statsReloadNonce } = useDoctorWorkspace();

  const [periodKey, setPeriodKey] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const periodDays = PERIOD_PRESETS.find((p) => p.key === periodKey)?.days || 7;
  const to = todayYmd();
  const from = daysAgoYmd(periodDays);

  const siteName = useMemo(() => {
    if (!workSiteId) return '';
    return (workSites || []).find((s) => String(s.id) === String(workSiteId))?.name || '';
  }, [workSiteId, workSites]);

  const load = useCallback(async () => {
    if (!user) return;
    if (!workSiteId) {
      setData(null);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await clinicalReportsService.getOverview({
        from,
        to,
        site_id: parseInt(workSiteId, 10),
        limit: 10
      });
      if (res.success) {
        setData(res.data);
      } else {
        setData(null);
        setError(H.ERROR_LOAD);
      }
    } catch {
      setData(null);
      setError(H.ERROR_LOAD);
    } finally {
      setLoading(false);
    }
  }, [user, workSiteId, from, to]);

  useEffect(() => {
    load();
  }, [load, statsReloadNonce]);

  const summary = data?.summary || {};
  const days = useMemo(
    () => countDaysInclusive(data?.range?.from || from, data?.range?.to || to),
    [data?.range?.from, data?.range?.to, from, to]
  );
  const periodLabel = `${from} → ${to}`;

  const synthesisGroups = useMemo(
    () => buildSynthesisRows(summary, data?.consultations?.by_service_line, days, HEAD_DOCTOR_PATHS),
    [summary, data?.consultations?.by_service_line, days]
  );

  const pendingCount = statusCount(data?.consultations?.by_status, 'PENDING');
  const nursingTotal = nursingActsTotal(summary);

  const topPathologies = data?.pathologies?.rows || [];
  const topMedications = useMemo(() => {
    return [...(data?.medications?.rows || [])].sort(
      (a, b) => Number(b.total_quantity || 0) - Number(a.total_quantity || 0)
    );
  }, [data?.medications?.rows]);
  const topPractitioners = data?.consultations?.top_doctors || [];

  return (
    <div className="hd-dash">
      <HeadDoctorPageHeader badge={H.BADGE} title={H.TITLE} description={H.SUBTITLE} />

      <div className="hd-dash-periodbar mb-3" role="group" aria-label={H.PERIOD_ARIA}>
        <div className="hd-dash-periodbar__presets">
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={`hd-dash-period${periodKey === preset.key ? ' is-active' : ''}`}
              onClick={() => setPeriodKey(preset.key)}
              disabled={loading}
              aria-pressed={periodKey === preset.key}
            >
              {H[preset.labelKey]}
            </button>
          ))}
        </div>
        <div className="hd-dash-periodbar__meta">
          {siteName ? (
            <span className="hd-dash-periodbar__chip">
              <Icon name="building" size={14} aria-hidden />
              {siteName}
            </span>
          ) : null}
          <span className="hd-dash-periodbar__chip">
            <Icon name="calendar" size={14} aria-hidden />
            {H.PERIOD_HINT(from, to)}
          </span>
        </div>
      </div>

      {!workSiteId ? (
        <Alert variant="warning" className="mb-4">
          {H.SITE_REQUIRED}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <section className="mb-4" aria-label={H.STATS_TITLE}>
        <div className="mb-3">
          <h2 className="hd-dash-primary__title mb-1">{H.STATS_TITLE}</h2>
          <p className="hd-dash-primary__desc mb-0">{H.STATS_SUBTITLE}</p>
        </div>
        <div className="hd-dash-kpi-grid">
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_CONSULTATIONS}>
            <HeadDoctorStatCard
              title={H.STAT_CONSULT_TOTAL}
              value={summary.consultations}
              icon="stethoscope"
              tone="indigo"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_CONSULTATIONS}>
            <HeadDoctorStatCard
              title={H.STAT_CONSULT_PENDING}
              value={pendingCount}
              icon="infoCircle"
              tone="amber"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_WORK_ACCIDENTS}>
            <HeadDoctorStatCard
              title={H.STAT_AT}
              value={summary.work_accidents}
              icon="alertCircle"
              tone="amber"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_SOINS}>
            <HeadDoctorStatCard
              title={H.STAT_NURSING}
              value={nursingTotal}
              icon="heart"
              tone="teal"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_EXAMENS}>
            <HeadDoctorStatCard
              title={H.STAT_EXAMS}
              value={summary.exam_orders}
              icon="chartBar"
              tone="slate"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_ORDONNANCES}>
            <HeadDoctorStatCard
              title={H.STAT_RX}
              value={summary.prescriptions}
              icon="pills"
              tone="teal"
              loading={loading}
            />
          </StatLink>
          <StatLink to={HEAD_DOCTOR_PATHS.STATISTIQUES_JOURS_REPOS}>
            <HeadDoctorStatCard
              title={H.STAT_REST}
              value={summary.rest_day_certificates}
              icon="calendar"
              tone="indigo"
              loading={loading}
            />
          </StatLink>
        </div>
      </section>

      {workSiteId ? (
        <>
          <DashboardHighlights
            pathologies={topPathologies}
            medications={topMedications}
            practitioners={topPractitioners}
            loading={loading}
          />
          <DashboardActivityPanel
            groups={synthesisGroups}
            days={days}
            periodLabel={periodLabel}
            loading={loading}
          />
        </>
      ) : null}

      <section className="hd-dash-reports mb-2" aria-label={H.REPORTS_TITLE}>
        <div className="mb-3">
          <h2 className="hd-dash-primary__title mb-1">{H.REPORTS_TITLE}</h2>
          <p className="hd-dash-primary__desc mb-0">{H.REPORTS_SUBTITLE}</p>
        </div>
        <div className="hd-dash-reports__grid">
          <Link to={HEAD_DOCTOR_PATHS.STATISTIQUES_SYNTHESE} className="hd-dash-report-card hd-dash-report-card--accent">
            <span className="hd-dash-report-card__icon" aria-hidden>
              <Icon name="chartBar" size={20} />
            </span>
            <span>
              <span className="hd-dash-report-card__title">{H.REPORTS_CTA_SYNTHESE}</span>
              <span className="hd-dash-report-card__hint">{H.REPORTS_CTA_SYNTHESE_HINT}</span>
            </span>
            <Icon name="chevronRight" size={18} className="hd-dash-report-card__chevron" aria-hidden />
          </Link>
          <Link to={HEAD_DOCTOR_PATHS.STATISTIQUES_CONSULTATIONS} className="hd-dash-report-card">
            <span className="hd-dash-report-card__title">{H.REPORTS_CTA_CONSULT}</span>
          </Link>
          <Link to={HEAD_DOCTOR_PATHS.STATISTIQUES_MORBIDITE} className="hd-dash-report-card">
            <span className="hd-dash-report-card__title">{H.REPORTS_CTA_MORBIDITE}</span>
          </Link>
          <Link to={HEAD_DOCTOR_PATHS.STATISTIQUES_ORDONNANCES} className="hd-dash-report-card">
            <span className="hd-dash-report-card__title">{H.REPORTS_CTA_RX}</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
